import { useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';

// Events that constitute "activity"
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'keypress'
];

interface UseActivityTrackerOptions {
  onActivity: () => void;
  isActive: boolean;
  debounceMs?: number;
}

/**
 * Hook to track user activity across the application.
 * Calls onActivity when user interacts with the page.
 */
export const useActivityTracker = ({ 
  onActivity, 
  isActive, 
  debounceMs = 1000 
}: UseActivityTrackerOptions) => {
  // Use ref for onActivity to avoid re-binding effects when function identity changes
  const onActivityRef = useRef(onActivity);

  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  // Create debounced handler
  const handleActivity = useCallback(
    debounce(() => {
      if (isActive) {
        onActivityRef.current();
      }
    }, debounceMs, { leading: true, trailing: false }),
    [isActive, debounceMs]
  );

  useEffect(() => {
    if (!isActive) return;

    // Attach event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    
    // Also track visibility changes (switching tabs back active)
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            handleActivity();
        }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup
      handleActivity.cancel();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, handleActivity]);
};
